import { mapState, mapGetters } from 'vuex'
import omit from 'lodash/omit'
import pick from '@/utils/transformers/pick'
import { checkMainLocations } from '@/utils/transformers/results'
import {
  icMap,
  icSave,
  icList,
  icFilter2,
  icCloseSmall,
  icArrowDown,
  icArrowUp,
} from '@vivareal/lina-icons'
import { getUF } from '@/filters/address'
import registerStoreModuleMixin from '@/mixins/register-store-module'
import localStorage from '@/storage/local-storage'
import tracker from '@/utils/tracking/tracker'
import backToTop from '@/utils/tracking/back-to-top-clicked'
import filterRemovedClicked from '@/utils/tracking/filter-clicked'
import { smoothScrollTo, hiddeBodyScroll } from '@/utils/scroll'
import { URLS, RESULTS } from '@/utils/constants'
import { getQueryByParams, getParamsByQuery } from '@/utils/urls/mapper'
import optimizeActivate from '@/mixins/optimize-activate'
import ozResults from '@/components/oz-results'
import { LEAD_FORM_SEE_PHONE_MODAL } from '@/store/experimentations/experiments'
import select from '@/utils/select'
import { getVariationOf } from '@/store/experimentations/selectors'
import ozBreadcrumb from '@/components/oz-breadcrumb'
import trackingPage from '@/utils/constants/tracking/tracking-page'
import ozMainLocations from '@/components/seo/oz-main-locations-poc'
import ozMapSearch from '@/components/oz-map-search'
import ozCardSkeleton from '@/components/oz-card/oz-card-skeleton'
import ozLoading from '@/components/oz-loading'
import ozQuickFilters from '@/components/filters/oz-quick-filters'
import abstractIsUserOnModelCity from '@/utils/campaign-location'
import ozSort from '@/components/oz-sort'
import renderWhenVisible from '@/components/render-when-visible'
import ozNoResults from '@/components/oz-no-results'
import ozNeighborhoodsGuideBanner from '@/components/oz-neighborhoods-guide-banner'
import hasSeoWidgets from '@/mixins/has-seo-widgets'
import { CTA_BUYING_POWER_CALCULATOR } from '@/store/experimentations'
import ozTitle from './components/summary/oz-title'
import trackingHelper from './tracking'
import ozFiltersContainer from './components/filters/oz-filters-container'
import recommendationsSettings from './oz-recommendations.settings'
import { PAGE_SIZE_DEFAULT } from '../../utils/constants/results'
import { buyingPowerCalculatorHeaderButtonClicked } from './components/filters/components/oz-buying-power-calculator-button/oz-buying-power-calculator-button.tracker'

const ozAds = () => import(/* webpackChunkName: "oz-ads" */'@/components/oz-ads')
const ozSeoWidgetsNeighborhoodGuide = () => import(/* webpackChunkName: "oz-seo-widgets-neighborhood-guide" */ '@/components/seo/oz-seo-widgets-neighborhood-guide')
const ozLeadModalNew = () => import(/* webpackChunkName: "oz-lead-modal-new" */ '@/components/lead/oz-lead-modal-new')
const ozConciergeSurvey = () => import(/* webpackChunkName: "oz-concierge-survey" */ '@/components/lead/oz-concierge-survey')
const ozPostLeadModal = () => import(/* webpackChunkName: "oz-post-lead-modal" */ '@/components/lead/oz-post-lead-modal')
const ozInactiveLeadModal = () => import(/* webpackChunkName: "oz-inactive-lead-modal" */ '@/components/lead/oz-inactive-lead-modal')
const ozHeaderAdvertiser = () => import(/* webpackChunkName: "oz-header-advertiser" */ './components/oz-header-advertiser')
const ozHeaderOfficialStore = () => import(/* webpackChunkName: "oz-header-official-store" */ './components/oz-header-official-store')
const ozSavedSearch = () => import(/* webpackChunkName: "oz-saved-search" */ './components/oz-saved-search')
const ozUserRecommendations = renderWhenVisible(() => import(/* webpackChunkName: "oz-user-recommendations" */'@/components/oz-user-recommendations'))
const ozPagination = renderWhenVisible(() => import(/* webpackChunkName: "oz-pagination" */ '@/components/oz-pagination'))

export default {
  name: 'oz-result-page',

  data () {
    return {
      sortOptions: RESULTS.SORT_OPTIONS.map((i) => ({ value: i.value, label: i.label })),
      announceZapUrl: `${URLS.ANNOUNCE_ZAP_URL}/vender-imovel/?itl_id=1000173&itl_name=zap_-_link_zap-imov-o-mel-lugar-p-ve-seu-ap-anun-ag_to_zap_venda-seu-imovel`,
      discoverZapUrl: `${URLS.ZAP_DISCOVER_URL}?itl_id=1000172&itl_name=zap_-_link_n-encon-o-que-busc-veja-o--que-o-zap-recom-p-vc_to_zap_descobrir`,
      // for tracking purposes
      recommendationsSettings,
      screen: 'RESULT PAGE',
      showFilters: false,
      showFiltersFixed: false,
      mapSearch: false,
      showRecommendationCTA: false,
      icons: {
        icCloseSmall,
        icArrowDown,
        icArrowUp,
      },
      showCampaignAlert: false,
      scrolled: 0,
    }
  },

  mounted () {
    window.addEventListener('scroll', this.handleScroll)

    this.showCampaignAlert = this.setVisibilityCampaignAlert(true)
    localStorage.setRankingId(this.filters)
  },

  mixins: [
    registerStoreModuleMixin,
    optimizeActivate,
    hasSeoWidgets,
  ],

  asyncData: async ({ store, route }) => {
    // ABOUT THIS:
    // We use this flag to control request using path,
    // E.g.: When we make a filter, glue returns a
    // new URL and we use router.push to update current
    // browser url but call again this function,
    // in this case we don’t need to fetch data again,
    // so we can skip this method.
    if (__BROWSER__ && window.skipAsyncDataRP) {
      window.skipAsyncDataRP = false
      return
    }

    const { path, query } = route
    const { glossary, unitTypeGlossary, featureToggle } = store.state.context

    const translatedQuery = getParamsByQuery(
      query,
      glossary,
      unitTypeGlossary,
    )

    store.commit('results/setShowMainLocations', checkMainLocations(query, path))

    await store.dispatch('results/listingsFetch', {
      currentPath: path,
      translatedQuery,
      routeQuery: query,
    })

    // Only request official store data to publisher with minisite configuration
    if (route.path.startsWith('/imobiliaria') && featureToggle.official_store && store.state.page.publisher.id) {
      await store.dispatch('officialStore/gettingOfficialStoreData', store.state.page.publisher.id)
    }
  },

  computed: {
    ...mapGetters({
      isMobile: 'isMobile',
      getWidgetByCategory: 'page/getWidgetByCategory',
      popup: 'map/getPopup',
      showModal: 'ui/modals/showModal',
      showInactiveLeadModal: 'ui/modals/showInactiveLeadModal',
      showPostLeadModal: 'ui/modals/showPostLeadModal',
      showConciergePostLeadModal: 'ui/modals/showConciergePostLeadModal',
      showSavedSearchModal: 'ui/modals/showSavedSearchModal',
      safeQuery: 'safeQuery',
      pageCategory: 'page/getCategory',
    }),

    ...mapState({
      glossary: (state) => state.context.glossary,
      storeData: (state) => state.officialStore.storeData,
      unitTypeGlossary: (state) => state.context.unitTypeGlossary,
      account: (state) => state.page.publisher,
      primaryHeading: (state) => state.page.metaContent?.primaryHeading ?? '',
      path: (state) => state.page.path,
      pageViewTrackingData: (state) => state.page.pageViewTrackingData,
      featureToggle: (state) => state.context.featureToggle,
      loading: (state) => state.results.loading,
      listings: (state) => state.results.listings,
      filters: (state) => state.results.filters,
      pagination: (state) => state.results.pagination,
      isMainLocationVisible: (state) => state.results.isMainLocationVisible,
      isShowMobileMapButton: (state) => state.results.isShowMobileMapButton,
      isShowRefineSearchOnMobile: (state) => state.results.isShowRefineSearchOnMobile,
      isMapOpen: (state) => state.results.isMapOpen,
      userLocation: (state) => state.userLocation.userLocation,
      pagePath: (state) => state.page.path,
      contentResultPage: (state) => state.results.contentResultPage,
      isOfficialStoreOn: (state) => state.context.featureToggle.official_store,
    }),

    leadPhoneModalVariation: select(
      getVariationOf(LEAD_FORM_SEE_PHONE_MODAL),
      ([name]) => name,
    ),

    isLeadPhoneModalVariation () {
      return this.leadPhoneModalVariation === 'modal'
    },

    filtersWithoutViewportProp () {
      // filters.viewport must not change URL
      return JSON.stringify({ ...this.filters, viewport: null })
    },
    buyingPowerCalculatorAB: select(
      getVariationOf(CTA_BUYING_POWER_CALCULATOR),
      ([name]) => name,
    ),
    getSourceRecommendations () {
      const { totalCount } = this.pagination
      const littleQuantityListing = totalCount <= PAGE_SIZE_DEFAULT
      if (littleQuantityListing) {
        this.showRecommendationCTA = true
        return 'EXPANSION'
      }

      this.showRecommendationCTA = false
      return 'SEARCH'
    },

    wrapperClass () {
      return {
        results__wrapper: true,
        'results__wrapper--map': this.mapSearch,
        'results__wrapper--select': this.popup.listing,
      }
    },

    ozFilters () {
      return omit(this.filters, RESULTS.FILTERS_OUT_FROM_OZ_FILTERS)
    },

    isAdvertiserPage () {
      return this.filters.categoryPage === 'PUBLISHER' && (!this.isOfficialStoreOn || !this.storeData)
    },

    isOfficialStore () {
      return this.filters.categoryPage === 'PUBLISHER' && this.isOfficialStoreOn && !!this.storeData
    },

    isResultPage () {
      return (!this.isAdvertiserPage || !this.isOfficialStore)
        && this.filters.categoryPage === 'RESULT'
    },

    howRefPage () {
      const emptyList = this.listings.length === 0
      if (emptyList) return trackingPage.REF_PAGE_EMPTY_SEARCH

      return trackingPage.REF_PAGE_RESULT_PAGE_SEARCH
    },

    noResultsTitle () {
      const { currentPage } = this.pagination
      const message = 'Oops. Não encontramos resultados para a busca:'

      return `${message} ${this.primaryHeading} na página ${currentPage}`
    },
    shouldRenderBuyingPowerCalculatorButton () {
      return this.ozFilters?.business === 'SALE' && this.buyingPowerCalculatorAB === 'header'
    },

    neighborhoodsGuideWidget () {
      const widget = this.getWidgetByCategory('NEIGHBORHOOD_GUIDE')
      const { addresses: [{ state = '', neighborhood = '' } = {}] = [] } = this.filters

      if (!widget) {
        return {}
      }

      return {
        name: widget?.header?.name?.replace(/(.*)bairro /, '') ?? 'Bairro',
        href: widget?.header?.href
          ? `${widget.header.href}?itl_id=1000075&itl_name=zap_botao-cta_bairros-${getUF(state, this.glossary)?.toLowerCase()}-${neighborhood?.toLowerCase()}_to_zap_guia-de-bairros`
          : '',
      }
    },

    shouldRenderAsLink () {
      const { addresses: [{ state = '' } = {}] = [] } = this.filters
      return this.featureToggle.zapRpWithLinks && state === 'Rio de Janeiro'
    },

    shouldRenderDynamicTitle () {
      const shouldRenderDynamicTest = this.featureToggle.zapRpWithDynamicCard
      const { addresses: [{ state = '' } = {}] = [] } = this.filters
      return shouldRenderDynamicTest && (state === 'Minas Gerais' || state === 'Goiás')
    },

    shouldRenderWithoutDescription () {
      const { addresses: [{ state = '' } = {}] = [] } = this.filters
      return state === 'Goiás'
    },

    // MAP SEARCH MARKERS
    markers () {
      const items = this.listings.filter((l) => {
        const { point } = l.listing.address
        return point && (point.lat && point.lon)
      })

      const locations = items.map((l) => {
        const {
          id,
          address: { point: { lat, lon } },
        } = l.listing

        return {
          id,
          position: { lat, lng: lon },
          info: {
            listing: l.listing,
            account: l.account,
            link: l.link,
          },
          listingsCount: l.listing.listingsCount,
        }
      })

      return locations
    },

    sumaryActions () {
      return {
        map: {
          icon: this.mapSearch ? icFilter2 : icMap,
          text: this.mapSearch ? 'Filtros' : 'Mapa',
        },
        saveSearch: {
          icon: icSave,
          text: 'Salvar busca',
        },
        buyingPowerCalculator: {
          text: 'Calcular meu poder de compra',
        },
      }
    },

    mobileActions () {
      return {
        filter: {
          icon: icFilter2,
          text: 'Filtrar',
        },
        map: {
          icon: this.mapSearch ? icList : icMap,
          text: this.mapSearch ? 'Lista' : 'Mapa',
        },
      }
    },

    isUserOnModelCity () {
      const localUserLocation = localStorage.get('userLocation').data

      return abstractIsUserOnModelCity(this.userLocation, localUserLocation)
    },

    showCampaignLinkBanner () {
      const isFeiraoPage = this.path.startsWith('/feirao')
      const hasBanner = this.contentResultPage.publicityContent.desktopImage
      return this.featureToggle.seasonal_campaign_active_viva && isFeiraoPage && hasBanner
    },

    shouldShowNeighborhoodsGuideBanners () {
      return this.pageCategory?.uriCategory?.levels?.includes('NEIGHBORHOOD') && !!Object.keys(this.neighborhoodsGuideWidget).length
    },

    buttonBackToTopClass () {
      return {
        results__actions__button: true,
        'results__actions__button--after-scroll': this.scrolled > 200,
        'results__actions__button--display-none': this.isShowRefineSearchOnMobile,
      }
    },

    mapButtonClass () {
      return {
        'results__actions--mobile': true,
        'results__actions--map': !this.isMapOpen,
        'results__actions--map-scrolled': this.scrolled > 200 && !this.isMapOpen,
      }
    },
  },

  watch: {
    filtersWithoutViewportProp () {
      this.updateRoute(this.filters, this.path)
    },

    loading (isLoading) {
      if (!isLoading) {
        localStorage.setRankingId(this.filters)
      }
    },
  },

  beforeRouteEnter (to, from, next) {
    if (__BROWSER__) {
      next((vm) => {
        vm.removeScrollDisable()
        vm.initTrackers(from)
      })
    } else {
      next()
    }
  },

  beforeRouteUpdate (to, from, next) {
    if (__BROWSER__) {
      this.initTrackers(from)
    }
    next()
  },

  methods: {
    handleScroll () {
      this.scrolled = window.scrollY
    },

    async onPillsUpdate (filters, removedFilterName) {
      const currentFilters = { ...this.filters }
      await this.onOzFiltersUpdate({ ...currentFilters, ...filters })

      try {
        this.trackRemovedFilters(currentFilters, filters, removedFilterName)
      } catch (error) {
        const { code, location } = error

        if (location) throw error

        this.$store.dispatch('errors/setHttpStatus', code, { root: true })
      }
    },

    trackRemovedFilters (filtersReference, filtersToBeRemoved, removedFilterName) {
      if (!removedFilterName || !filtersToBeRemoved || !filtersReference) return
      let filtersToSend

      if (removedFilterName === 'clearAllFilters') {
        const filtersApply = pick(filtersReference, Object.keys(filtersToBeRemoved))
        const filtersHasValue = Object.keys(filtersApply)
          .filter((filter) => filtersApply[filter])

        filtersToSend = filtersHasValue.sort().join()
      } else {
        filtersToSend = Object.keys(filtersToBeRemoved).sort().join()
      }

      const event = filterRemovedClicked(removedFilterName, filtersToSend)
      this.$clickstream.trackEvent(event)
    },

    async onSortChange (sort) {
      await this.$store.commit('results/paginate', 1)
      await this.$store.dispatch('results/sort', sort)
    },

    async onPageChange (page) {
      smoothScrollTo()
      await this.$store.dispatch('results/paginate', page)
    },

    updateRoute (filterParams, path = this.$route.path) {
      const { currentPage: page } = this.pagination
      const params = page > 1 ? ({ page, ...filterParams }) : filterParams
      const { __zt } = this.safeQuery

      const query = {
        __zt,
        ...getQueryByParams(params, this.glossary, this.unitTypeGlossary),
      }

      this.$store.commit('results/setShowMainLocations', checkMainLocations(query, path))
      window.skipAsyncDataRP = true
      this.$router.push({ path, query })
    },

    removeScrollDisable () {
      if (document.getElementById('app')?.getAttribute('scroll-disable')) {
        document.getElementById('app')?.removeAttribute('scroll-disable')
      }
    },

    updateAddressesUrl (addresses, listingType, business) {
      if (!addresses?.length) {
        return addresses
      }

      const updateAddresses = []
      const developmentUrlPath = 'lancamentos'

      const businessType = {
        RENTAL: 'aluguel',
        SALE: 'venda',
      }

      const newUrlPath = {
        aluguel: 'venda',
        venda: 'aluguel',
        lancamentos: businessType[business],
      }

      addresses.forEach((address) => {
        const regexPath = /[^/]\w*/
        const { url = '' } = address
        if (!url) {
          updateAddresses.push(address)
          return
        }
        const path = regexPath.exec(url)[0]
        if (listingType === 'DEVELOPMENT') {
          updateAddresses.push({ ...address, url: url.replace(regexPath, developmentUrlPath) })
        } else {
          updateAddresses.push({ ...address, url: url.replace(regexPath, newUrlPath[path]) })
        }
      })

      return updateAddresses
    },

    async onOzFiltersUpdate (newOzFilters) {
      const page = 1

      const { pageSize: size } = this.pagination
      const from = size * (page - 1)

      let params = {
        ...newOzFilters,
        ...pick(this.filters, RESULTS.FILTERS_OUT_FROM_OZ_FILTERS),
        page,
        from,
        size,
      }

      const { business: businessNewFilter, listingType: listingTypeNewFilter } = newOzFilters
      const { business, listingType } = this.filters

      //business or listingType filter change, update addressesUrl
      if (businessNewFilter !== business || listingTypeNewFilter !== listingType) {
        const newAddresses = this.updateAddressesUrl(
          params.addresses,
          listingTypeNewFilter,
          businessNewFilter,
        )

        params = { ...params, addresses: newAddresses }
      }

      await this.$store.dispatch('results/search', params)
    },

    initTrackers (from) {
      if (!__BROWSER__) return
      const eventData = trackingHelper.pageView(this.pageViewTrackingData)
      tracker.trackPageView(this.$store, eventData)
      // TODO when all pages has its tracking refactored
      // this generic event can be moved to a route guard like `afterEach`
      tracker.trackPageRendered()
      tracker.track(trackingHelper.adsConfigSlotReady(this.filters.addresses))
      if (from.name) {
        tracker.trackAdsRefreshed(trackingHelper.adElementIds)
      }
    },

    onOzFiltersToggle (showFilters, showFiltersFixed) {
      this.showFilters = showFilters
      this.showFiltersFixed = showFiltersFixed
    },

    onOzFiltersMap () {
      this.$store.commit('results/setIsMapOpen', !this.isMapOpen)
      this.mapSearch = !this.mapSearch
      this.$refs.mapSearch.init()
    },

    onBackToTop () {
      backToTop.trackBackToTopClicked()

      window.scrollTo({
        top: 0,
        left: 0,
      })
    },

    redirectToBuyingPowerCalculator () {
      const { query } = this.$route

      buyingPowerCalculatorHeaderButtonClicked()
      this.$router.push({ name: 'buying-power', query: { ...query, from: 'RP-header' } })
    },

    filterButtonClick () {
      this.$store.commit('results/setIsShowRefineSearchOnMobile', !this.isShowRefineSearchOnMobile)
      this.onOzFiltersToggle(true, true)
      hiddeBodyScroll()
    },

    mapButtonAreaClick () {
      const { mapSearch } = this.$refs

      const filters = { ...this.filters }
      delete filters.addresses

      if (mapSearch) {
        const bounds = mapSearch.getBounds()
        const southWest = bounds.getSouthWest().toJSON()
        const northEast = bounds.getNorthEast().toJSON()
        filters.viewport = `${northEast.lng},${northEast.lat}|${southWest.lng},${southWest.lat}`

        mapSearch.tracker().events('SEARCHED_AREA', {}, [southWest, northEast])
        this.$store.dispatch('results/search', filters)
      }
    },

    mapEvents (event, marker) {
      const interactionEvents = ['MAP_CLICKED', 'MARKER_CLICKED']

      if (interactionEvents.includes(event)) {
        this.selectCard(marker)
      }
    },

    selectCard ({ id } = {}) {
      if (id) {
        const container = document.querySelector(`[data-id="${id}"]`)

        if (container) {
          container.scrollIntoView({ block: 'center', behavior: 'instant' })
        }
      }
    },

    savedSearchButtonClick () {
      this.$store.commit('ui/modals/setSavedSearchModal', {
        savedSearchModal: { show: true },
      })
    },

    setVisibilityCampaignAlert (show) {
      const withoutWarranties = this.filters.warranties === 'zapway'

      return show && (this.isUserOnModelCity && withoutWarranties)
    },

    closeCampaignAlert () {
      this.showCampaignAlert = this.setVisibilityCampaignAlert(false)
    },
  },

  components: {
    ozPagination,
    ozResults,
    ozLoading,
    ozSort,
    ozTitle,
    ozAds,
    ozCardSkeleton,
    ozMapSearch,
    ozSavedSearch,
    ozUserRecommendations,
    ozHeaderAdvertiser,
    ozHeaderOfficialStore,
    ozMainLocations,
    ozSeoWidgetsNeighborhoodGuide,
    ozNeighborhoodsGuideBanner,
    ozLeadModalNew,
    ozConciergeSurvey,
    ozPostLeadModal,
    ozInactiveLeadModal,
    ozBreadcrumb,
    ozFiltersContainer,
    ozQuickFilters,
    ozNoResults,
  },
}
